import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Globe, CheckCircle2, XCircle, RefreshCw, Send, ShoppingBag,
  FileText, Users, ExternalLink, Loader2, Sparkles, Building2,
} from "lucide-react";

const HEALTH_ROWS = [
  { key: "reachable", label: "REST API joignable" },
  { key: "authenticated", label: "Authentifié (App Password)" },
  { key: "has_woocommerce", label: "WooCommerce installé" },
  { key: "has_events_calendar", label: "Events Calendar installé" },
  { key: "has_email_subscribers", label: "Email Subscribers installé" },
  { key: "has_fooevents", label: "FooEvents installé" },
];

export default function WordPressSyncModule() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({ tarifs: false, essai: false, leads: false, blog: false });
  const [lastResults, setLastResults] = useState({});

  // Blog form
  const [blog, setBlog] = useState({ slug: "", title: "", content_html: "", excerpt: "", status: "draft" });

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wp/health");
      setHealth(data);
    } catch (e) {
      toast.error("Impossible de joindre WordPress");
      setHealth({ configured: false, error: e?.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  const runSync = async (key, endpoint, method = "post", body = null) => {
    setSyncing((s) => ({ ...s, [key]: true }));
    try {
      const { data } = method === "post" ? await api.post(endpoint, body) : await api.get(endpoint);
      setLastResults((r) => ({ ...r, [key]: data }));
      toast.success("Synchronisation réussie ✓");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur de synchronisation");
      setLastResults((r) => ({ ...r, [key]: { error: detail || e.message } }));
    }
    setSyncing((s) => ({ ...s, [key]: false }));
  };

  const syncBlog = async (e) => {
    e.preventDefault();
    if (!blog.slug || !blog.title || !blog.content_html) {
      toast.error("Slug, titre et contenu requis");
      return;
    }
    await runSync("blog", "/wp/sync/blog", "post", blog);
  };

  return (
    <div className="space-y-8 p-6" data-testid="wp-sync-dashboard">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Globe className="w-8 h-8 text-amber-400" />
            WordPress · Synchronisation
          </h1>
          <p className="text-zinc-400 mt-1">
            Pilote ton site <span className="text-amber-300">institutimpactelitemartial.com</span> depuis Mon Assistant Pro.
          </p>
        </div>
        <button
          onClick={loadHealth}
          disabled={loading}
          data-testid="wp-refresh-btn"
          className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded-lg border border-zinc-700"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
        </button>
      </div>

      {/* STATUS CARD */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-6" data-testid="wp-status-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-400" /> État de la connexion
          </h2>
          {health?.configured && health?.authenticated && (
            <span className="text-emerald-400 text-sm flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Connecté
            </span>
          )}
        </div>

        {loading && !health ? (
          <div className="flex items-center gap-2 text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Test de connexion…
          </div>
        ) : !health?.configured ? (
          <div className="text-red-400">
            ⚠ Variables d'environnement WP non configurées dans <code>backend/.env</code>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {HEALTH_ROWS.map((row) => (
                <div key={row.key} className="flex items-center gap-2" data-testid={`wp-check-${row.key}`}>
                  {health?.[row.key] ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  )}
                  <span className={health?.[row.key] ? "text-zinc-200" : "text-zinc-500"}>{row.label}</span>
                </div>
              ))}
            </div>
            {health?.wp_user && (
              <div className="mt-4 pt-4 border-t border-zinc-800 text-sm text-zinc-400">
                Authentifié en tant que <strong className="text-amber-300">{health.wp_user.slug}</strong>
                {health.wp_user.is_admin && <span className="ml-2 px-2 py-0.5 bg-amber-900/40 text-amber-300 rounded text-xs">super-admin</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* QUICK SYNC ACTIONS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* TARIFS */}
        <ActionCard
          icon={<FileText className="w-5 h-5 text-amber-400" />}
          title="Sync grille de tarifs → /tarifs/"
          description="Pousse tous tes tarifs actifs (dojo_tariffs) sur la page WordPress Tarifs."
          buttonLabel="Synchroniser les tarifs"
          buttonTestId="wp-sync-tarifs-btn"
          loading={syncing.tarifs}
          onClick={() => runSync("tarifs", "/wp/sync/tarifs")}
          result={lastResults.tarifs}
        />
        {/* COURS ESSAI */}
        <ActionCard
          icon={<Sparkles className="w-5 h-5 text-amber-400" />}
          title="Sync page Cours d'essai (19,98 $)"
          description="Crée ou met à jour la page /cours-essai/ avec le CTA vers ton appli."
          buttonLabel="Synchroniser la page"
          buttonTestId="wp-sync-essai-btn"
          loading={syncing.essai}
          onClick={() => runSync("essai", "/wp/sync/cours-essai")}
          result={lastResults.essai}
        />
        {/* LEADS PULL */}
        <ActionCard
          icon={<Users className="w-5 h-5 text-amber-400" />}
          title="Importer les leads WordPress → CRM"
          description="Aspire les abonnés Email Subscribers / Icegram vers la collection CRM interne."
          buttonLabel="Importer les abonnés"
          buttonTestId="wp-pull-leads-btn"
          loading={syncing.leads}
          onClick={() => runSync("leads", "/wp/leads/pull?limit=200", "get")}
          result={lastResults.leads}
        />
        {/* WOO PRODUCTS */}
        <ActionCard
          icon={<ShoppingBag className="w-5 h-5 text-amber-400" />}
          title="Lister les produits WooCommerce"
          description="Vérifie que les produits boutique WooCommerce du site répondent correctement."
          buttonLabel="Lister les produits"
          buttonTestId="wp-list-products-btn"
          loading={syncing.products}
          onClick={() => runSync("products", "/wp/woocommerce/products?per_page=10", "get")}
          result={lastResults.products}
        />
      </div>

      {/* BLOG PUBLISH */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-6" data-testid="wp-blog-publish-card">
        <h2 className="text-xl font-semibold flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-amber-400" /> Publier un article de blog
        </h2>
        <form onSubmit={syncBlog} className="grid gap-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              data-testid="wp-blog-slug-input"
              placeholder="slug (ex: muay-thai-fondations)"
              value={blog.slug}
              onChange={(e) => setBlog({ ...blog, slug: e.target.value })}
              className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2"
            />
            <input
              data-testid="wp-blog-title-input"
              placeholder="Titre de l'article"
              value={blog.title}
              onChange={(e) => setBlog({ ...blog, title: e.target.value })}
              className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2"
            />
          </div>
          <textarea
            data-testid="wp-blog-content-input"
            placeholder="Contenu HTML de l'article (paragraphes, h2, listes…)"
            value={blog.content_html}
            onChange={(e) => setBlog({ ...blog, content_html: e.target.value })}
            rows={6}
            className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 font-mono text-sm"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              data-testid="wp-blog-excerpt-input"
              placeholder="Résumé (excerpt)"
              value={blog.excerpt}
              onChange={(e) => setBlog({ ...blog, excerpt: e.target.value })}
              className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2"
            />
            <select
              data-testid="wp-blog-status-select"
              value={blog.status}
              onChange={(e) => setBlog({ ...blog, status: e.target.value })}
              className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2"
            >
              <option value="draft">Brouillon</option>
              <option value="publish">Publié</option>
              <option value="private">Privé</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={syncing.blog}
            data-testid="wp-blog-publish-btn"
            className="flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {syncing.blog ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Publier sur WordPress
          </button>
        </form>
        {lastResults.blog && (
          <ResultBlock label="Dernière publication" data={lastResults.blog} testId="wp-blog-result" />
        )}
      </div>
    </div>
  );
}

function ActionCard({ icon, title, description, buttonLabel, buttonTestId, loading, onClick, result }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 flex flex-col" data-testid={`wp-action-${buttonTestId}`}>
      <div className="flex items-center gap-2 mb-2">{icon}<h3 className="font-semibold">{title}</h3></div>
      <p className="text-sm text-zinc-400 mb-4 flex-grow">{description}</p>
      <button
        onClick={onClick}
        disabled={loading}
        data-testid={buttonTestId}
        className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-amber-600 hover:text-black border border-zinc-700 hover:border-amber-500 transition-colors px-4 py-2 rounded-lg disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {buttonLabel}
      </button>
      {result && <ResultBlock data={result} testId={`${buttonTestId}-result`} />}
    </div>
  );
}

function ResultBlock({ label, data, testId }) {
  const link = data?.wordpress?.link;
  const products = data?.products;
  return (
    <div className="mt-4 pt-4 border-t border-zinc-800 text-sm" data-testid={testId}>
      {label && <div className="text-zinc-500 mb-1">{label}</div>}
      {data?.ok && data?.synced_count !== undefined && (
        <div className="text-emerald-400">✓ {data.synced_count} éléments synchronisés</div>
      )}
      {data?.imported !== undefined && (
        <div className="text-emerald-400">✓ {data.imported} importés · {data.skipped_existing} ignorés (existants)</div>
      )}
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-amber-300 hover:underline flex items-center gap-1 mt-1">
          <ExternalLink className="w-3 h-3" /> Voir sur WordPress
        </a>
      )}
      {products && products.length > 0 && (
        <ul className="mt-2 space-y-1 text-zinc-300">
          {products.slice(0, 6).map((p) => (
            <li key={p.id} className="truncate">• {p.name} <span className="text-zinc-500">— {p.price || "—"} $</span></li>
          ))}
        </ul>
      )}
      {data?.error && (
        <div className="text-red-400 text-xs mt-1">
          {typeof data.error === "string" ? data.error : JSON.stringify(data.error).slice(0, 200)}
        </div>
      )}
    </div>
  );
}
